import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import {
  channelMemberGuard,
  workspaceMemberGuard,
  workspaceMemberRowVisibility,
  workspaceMemberRowVisibilityAcrossWorkspaces,
} from "./access-filters";

export const workspaceMembersQueries = {
  // `byWorkspace` is the workspace's ACTIVE roster — every consumer
  // (admin members dialog, can-post check, sidebar workspace data,
  // mention picker, list-view assignees, etc.) wants live members
  // only. Excludes soft-deleted (kicked) rows by default so a
  // kicked user does NOT linger in every membership-rendering UI
  // after the kick lands.
  //
  // Soft-deleted rows are still resolvable for historical lookups
  // via `userAccessFilter` (which inspects the target user's
  // `workspace_memberships` relation directly, without going through
  // this query). If a future consumer needs the kicked rows too,
  // add a separate `byWorkspaceIncludingRemoved` query rather than
  // weakening this one.
  byWorkspace: defineQuery(z.string(), ({ args: wsId, ctx: { sub } }) =>
    zql.workspace_members
      .where("workspace_id", wsId)
      .where("removed_at", "IS", null)
      .where(workspaceMemberRowVisibility(sub, wsId)),
  ),
  // `byUser` intentionally does NOT filter `removed_at` so the
  // kicked user can detect their own tombstone row and the sidebar's
  // `useSidebarMembershipLossDetection` fires the "no longer a
  // member" dialog. Don't add the filter here.
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.workspace_members.where("user_id", sub),
  ),
  // `forUser(targetUserId)` returns the TARGET user's live memberships
  // in workspaces I'm also in (used by DMRowItem to render shared-
  // workspace badges). Filter `removed_at IS NULL` on the TARGET row
  // so kicked memberships don't keep showing up as "shared workspace".
  forUser: defineQuery(z.string(), ({ args: userId, ctx: { sub } }) =>
    zql.workspace_members
      .where("user_id", userId)
      .where("removed_at", "IS", null)
      .whereExists("workspace", workspaceMemberGuard(sub)),
  ),
  /**
   * All members across all workspaces the current user belongs to (for Home
   * DM grouping). Per-row visibility (`workspaceMemberRowVisibilityAcrossWorkspaces`)
   * is mandatory: the bare `workspaceMemberGuard` only checks the requester
   * is *a* member of the row's workspace, which leaks the full roster to
   * single-channel collaborators. The row filter restricts collaborators to
   * their own row + channel-sharing peers (full members still see everyone).
   */
  allSharedWorkspaceMembers: defineQuery(({ ctx: { sub } }) =>
    zql.workspace_members
      .where("removed_at", "IS", null)
      .whereExists("workspace", workspaceMemberGuard(sub))
      .where(workspaceMemberRowVisibilityAcrossWorkspaces(sub)),
  ),
};

export const channelMembersQueries = {
  // `byChannel` returns the channel's ACTIVE roster. Same reasoning
  // as `workspaceMembers.byWorkspace`: every UI consumer wants live
  // members, and a removed (`removed_at != null`) row should not
  // continue to show up in member lists, mention pickers, etc.
  byChannel: defineQuery(z.string(), ({ args: chId, ctx: { sub } }) =>
    zql.channel_members
      .where("channel_id", chId)
      .where("removed_at", "IS", null)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
  // Same `byUser` exception as workspace_members — kept unfiltered so
  // the kicked user can see their own tombstone row and detect they
  // were removed.
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.channel_members.where("user_id", sub),
  ),
  byChannelAndUser: defineQuery(
    z.object({ channelId: z.string(), userId: z.string() }),
    ({ args: { channelId, userId }, ctx: { sub } }) =>
      zql.channel_members
        .where("channel_id", channelId)
        .where("user_id", userId)
        .where("removed_at", "IS", null)
        .whereExists("channel", channelMemberGuard(sub))
        .one(),
  ),
  all: defineQuery(({ ctx: { sub } }) =>
    zql.channel_members
      .where("removed_at", "IS", null)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
  managers: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.channel_members
      .where("channel_id", channelId)
      .where("removed_at", "IS", null)
      .where("is_channel_manager", true)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
};
